/**
 * Validador de Anúncios — cálculo da tela (sem React/MUI).
 * A regra de negócio (o que é divergência, severidade, provável erro) é do serviço Python
 * (services/validador-anuncios); aqui só filtro, contagem e rótulos.
 */
import type {
  AnuncioQualidade, AnuncioValidado, Campo, Divergencia, Estado, EstadoQualidade, NivelQualidade, Pendencia, Severidade,
} from './types'

export const DIMENSAO_LABEL: Record<string, string> = {
  quantidade: 'Quantidade',
  pessoas: 'Pessoas',
  volume: 'Volume',
  medida: 'Medida',
  cor: 'Cor',
  'composição': 'Composição',
  'sem descrição': 'Sem descrição',
  outro: 'Outro',
}

export const CAMPOS: Array<[Campo, string]> = [
  ['titulo', 'título'],
  ['ficha', 'ficha'],
  ['descricao', 'descrição'],
]

export const CAMPO_LABEL: Record<Campo, string> = {
  titulo: 'Título',
  ficha: 'Ficha técnica',
  descricao: 'Descrição',
  imagem: 'Imagem',
}

export const SEVERIDADES: Severidade[] = ['alta', 'média', 'baixa']
export const SEVERIDADE_LABEL: Record<Severidade, string> = { alta: 'Alta', 'média': 'Média', baixa: 'Baixa' }
/** Cor do tema MUI por severidade (nunca cor escrita à mão). */
export const SEVERIDADE_COR: Record<Severidade, 'error' | 'warning' | 'default'> = {
  alta: 'error',
  'média': 'warning',
  baixa: 'default',
}
const ORDEM_SEV: Record<string, number> = { alta: 0, 'média': 1, baixa: 2 }

export type Aba = Estado | 'todos'
export const ABAS: Array<{ id: Aba; label: string }> = [
  { id: 'pendente', label: 'Pendentes' },
  { id: 'aguardando', label: 'Aguardando confirmação' },
  { id: 'corrigido', label: 'Corrigidos' },
  { id: 'falso', label: 'Falso positivo' },
  { id: 'todos', label: 'Todos' },
]

export const STATUS_ANUNCIO: Record<string, string> = {
  active: 'Ativo',
  paused: 'Pausado',
  under_review: 'Em revisão',
  inactive: 'Inativo',
}

/** Filtros comuns às abas Qualidade e Divergências (pedido do Felipe, 15/10): SKU, título, ID, canal, loja. */
export type FiltroAnuncio = { sku: string; titulo: string; id: string; canal: string; loja: string }
export const FILTRO_ANUNCIO_VAZIO: FiltroAnuncio = { sku: '', titulo: '', id: '', canal: '', loja: '' }

type AnuncioFiltravel = {
  id: string
  titulo: string
  sku?: string
  skus_variacao?: string[]
  marketplace: string
  loja_key?: string
}

export function passaFiltroAnuncio(a: AnuncioFiltravel, f: FiltroAnuncio): boolean {
  const sku = f.sku.trim().toLowerCase()
  if (sku && ![a.sku || '', ...(a.skus_variacao || [])].some((s) => s.toLowerCase().includes(sku))) return false
  const titulo = f.titulo.trim().toLowerCase()
  if (titulo && !a.titulo.toLowerCase().includes(titulo)) return false
  const id = f.id.trim().toLowerCase()
  if (id && !String(a.id).toLowerCase().includes(id)) return false
  if (f.canal && a.marketplace !== f.canal) return false
  if (f.loja && (a.loja_key || '') !== f.loja) return false
  return true
}

export function contaFiltroAnuncio(f: FiltroAnuncio): number {
  return Object.values(f).filter((v) => String(v).trim()).length
}

export type Filtros = { severidade: Severidade | ''; dimensao: string }
export const FILTROS_VAZIOS: Filtros = { severidade: '', dimensao: '' }

/** Forma comparável de um valor ("300un" == "300 un" == "300") — igual a chave_valor() do serviço. */
export function chaveValor(valor: string): string {
  const nums = String(valor).match(/\d+(?:[.,]\d+)?/g)
  return nums
    ? nums.map((n) => parseFloat(n.replace(',', '.'))).sort((a, b) => a - b).join('|')
    : String(valor).toLowerCase().trim()
}

export type ParteResumo = { texto: string; erro: boolean }

/** "título e descrição 100 un" ≠ "ficha 2 un": campos que concordam ficam juntos; o provável erro é marcado. */
export function partesDivergencia(d: Divergencia): ParteResumo[] {
  if (d.dimensao === 'sem descrição') return [{ texto: 'anúncio ativo com a descrição vazia', erro: false }]
  const grupos: Array<{ key: string; campos: string[]; valor: string; erro: boolean }> = []
  for (const [k, rotulo] of CAMPOS) {
    const c = d.campos[k]
    if (!c) continue
    const key = chaveValor(c.valor)
    let g = grupos.find((x) => x.key === key)
    if (!g) {
      g = { key, campos: [], valor: c.valor, erro: false }
      grupos.push(g)
    }
    g.campos.push(rotulo)
    if (d.provavel_erro === k) g.erro = true
  }
  return grupos.map((g) => ({ texto: `${g.campos.join(' e ')} ${g.valor}`, erro: g.erro }))
}

export function divergenciasOrdenadas(a: AnuncioValidado): Divergencia[] {
  return [...a.divergencias].sort((x, y) => (ORDEM_SEV[x.severidade] ?? 3) - (ORDEM_SEV[y.severidade] ?? 3))
}

export function aplicaFiltros(lista: AnuncioValidado[], aba: Aba, f: Filtros, fa: FiltroAnuncio): AnuncioValidado[] {
  return lista.filter(
    (a) =>
      (aba === 'todos' || a.estado === aba) &&
      (!f.severidade || a.divergencias.some((d) => d.severidade === f.severidade)) &&
      (!f.dimensao || a.divergencias.some((d) => d.dimensao === f.dimensao)) &&
      passaFiltroAnuncio({ ...a, loja_key: a.loja_key || '777058595' }, fa),
  )
}

export function contaFiltros(f: Filtros): number {
  return (f.severidade ? 1 : 0) + (f.dimensao ? 1 : 0)
}

/** Chave estável de um anúncio na tela (uid; análise antiga do ML só tinha id). */
export function chaveAnuncio(a: { uid?: string; id: string }): string {
  return a.uid || a.id
}

export function contaPorEstado(lista: AnuncioValidado[], corrigidos: number): Record<Aba, number> {
  const n = (e: Estado) => lista.filter((a) => a.estado === e).length
  return {
    pendente: n('pendente'),
    aguardando: n('aguardando'),
    corrigido: corrigidos,
    falso: n('falso'),
    todos: lista.length,
  }
}

/** Severidade máxima de cada anúncio ainda não resolvido (pendente ou aguardando). */
export function contaPorSeveridade(lista: AnuncioValidado[]): Record<Severidade, number> {
  const abertos = lista.filter((a) => a.estado === 'pendente' || a.estado === 'aguardando')
  return {
    alta: abertos.filter((a) => a.severidade_max === 'alta').length,
    'média': abertos.filter((a) => a.severidade_max === 'média').length,
    baixa: abertos.filter((a) => a.severidade_max === 'baixa').length,
  }
}

export function dimensoesPresentes(lista: AnuncioValidado[]): Array<{ id: string; n: number }> {
  const cont = new Map<string, number>()
  for (const a of lista) for (const d of new Set(a.divergencias.map((x) => x.dimensao))) cont.set(d, (cont.get(d) || 0) + 1)
  return [...cont.entries()].map(([id, n]) => ({ id, n })).sort((a, b) => b.n - a.n)
}

/** Nome amigável da IA que revisa os casos inconclusivos ("claude-haiku-4-5" → "Claude Haiku"). */
export function rotuloIa(ia: string | null | undefined): string {
  if (!ia) return ''
  if (ia === 'groq') return 'Groq'
  if (ia.startsWith('claude-haiku')) return 'Claude Haiku'
  if (ia.startsWith('claude')) return 'Claude'
  return ia
}

// ── Aba Qualidade ────────────────────────────────────────────────────────────

export const NIVEIS: Array<NivelQualidade | 'sem'> = ['excelente', 'bom', 'regular', 'ruim', 'sem']
export const NIVEL_LABEL: Record<NivelQualidade | 'sem', string> = {
  excelente: 'Excelente', bom: 'Bom', regular: 'Regular', ruim: 'Ruim', sem: 'Sem nota',
}
/** Cor do tema MUI por nível (nunca cor escrita à mão). */
export const NIVEL_COR: Record<NivelQualidade | 'sem', 'secondary' | 'success' | 'warning' | 'error' | 'default'> = {
  excelente: 'secondary', bom: 'success', regular: 'warning', ruim: 'error', sem: 'default',
}
const ORDEM_NIVEL: Record<string, number> = { ruim: 0, regular: 1, sem: 2, bom: 3, excelente: 4 }

export function pendenciasVisiveis(a: AnuncioQualidade, comercial: boolean): Pendencia[] {
  return a.pendencias.filter((p) => comercial || p.grupo !== 'comercial')
}

export function temAlerta(a: AnuncioQualidade, comercial: boolean): boolean {
  return pendenciasVisiveis(a, comercial).some((p) => p.modo === 'alerta')
}

export function ordenarQualidade(lista: AnuncioQualidade[], comercial: boolean): AnuncioQualidade[] {
  const al = (a: AnuncioQualidade) => pendenciasVisiveis(a, comercial).filter((p) => p.modo === 'alerta').length
  return [...lista].sort(
    (x, y) =>
      al(y) - al(x) ||
      (ORDEM_NIVEL[x.nivel || 'sem'] ?? 2) - (ORDEM_NIVEL[y.nivel || 'sem'] ?? 2) ||
      pendenciasVisiveis(y, comercial).length - pendenciasVisiveis(x, comercial).length ||
      (y.vendas || 0) - (x.vendas || 0),
  )
}

export type InstrucaoTop = { texto: string; modo: Pendencia['modo']; n: number; lojas: string[] }

/** Instruções iguais em vários anúncios (para corrigir em lote), mais repetidas primeiro. */
export function topInstrucoes(lista: AnuncioQualidade[], comercial: boolean): InstrucaoTop[] {
  const m = new Map<string, { texto: string; modo: Pendencia['modo']; n: number; lojas: Set<string> }>()
  for (const a of lista) {
    for (const t of new Set(pendenciasVisiveis(a, comercial).map((p) => p.texto_pt))) {
      const p = pendenciasVisiveis(a, comercial).find((x) => x.texto_pt === t)!
      const o = m.get(t) || { texto: t, modo: p.modo, n: 0, lojas: new Set<string>() }
      o.n += 1
      o.lojas.add(a.loja)
      m.set(t, o)
    }
  }
  return [...m.values()].map((o) => ({ ...o, lojas: [...o.lojas] })).sort((a, b) => b.n - a.n)
}

export function contaQualidadePorEstado(lista: AnuncioQualidade[], resolvidos: number): Record<EstadoQualidade, number> {
  const n = (e: EstadoQualidade) => lista.filter((a) => a.estado === e).length
  return { aberto: n('aberto'), aguardando: n('aguardando'), resolvido: resolvidos, ignorado: n('ignorado') }
}
